import posthog from 'posthog-js';
import React from 'react';

import {LoggedInAuth, useAuth} from 'app/providers/AuthProvider';
import {isProd} from 'app/utils/envUtils';
import {getLensTier} from 'app/utils/organizationUtils';
import {isUpstreamEmail} from 'app/utils/userUtils';

// Set to true for testing
const CAPTURE_ME_PLEASE = false;

const POSTHOG_KEY = 'phc_he5ukraFzEqMLOKXr9i2nDqDa3KhHqviNubB9ZWazt1';
const POSTHOG_HOST = 'https://us.i.posthog.com';

// this is a convenience to give the recordEvent and onboarding flows easy
// access without having to dance props around and create cumbersome required
// props everywhere. I prefer to limit the surface area of business logic changes like this.
let analyticsAuth: LoggedInAuth | undefined = undefined;

const Analytics: React.FunctionComponent = () => {
  const auth = useAuth();

  React.useEffect(() => {
    if (auth.status === 'logged in') {
      analyticsAuth = auth;
    }
  }, [auth]);

  let profile, profileOrganizations, firebaseUid, profileOrganization;

  if (auth.status === 'logged in') {
    profile = auth.profile;
    profileOrganizations = auth.profileOrganizations;
    firebaseUid = auth.firebaseUid;
    const organizationKey = profile?.get('organizationKey');
    if (organizationKey)
      profileOrganization = profileOrganizations?.find((org) => org?.get('id') === organizationKey);
  }

  React.useEffect(() => {
    if (!firebaseUid || !profileOrganization || !profile) {
      return;
    }

    if ((isProd && !isUpstreamEmail(profile.get('email'))) || CAPTURE_ME_PLEASE) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
        autocapture: false,
      });

      posthog.identify(firebaseUid, {
        userId: profile.get('id'),
        email: profile.get('email'),
        role: profile.get('role'),
        organizationId: profileOrganization.get('id'),
        organization: profileOrganization.get('name'),
        organizationPlan: getLensTier(profileOrganization),
      });

      posthog.group('organization', profileOrganization.get('id'), profileOrganization.toJS());

      return () => {
        posthog.reset();
      };
    }
  }, [firebaseUid, profile, profileOrganization]);

  return null;
};

export default Analytics;

// The only events a logged-out viewer of a share link sends, so what they do in the shared
// map and slides stays out of product usage metrics.
const PUBLIC_VIEWER_EVENTS: ReadonlySet<Events> = new Set(['Viewed public story']);

let isPublicViewer = false;

/**
 * Loads PostHog for a logged-out viewer of a share link: no person profile, no IP address, and
 * nothing stored in their browser, since these pages are often embedded on other sites. A
 * signed-in viewer is left to Analytics, which identifies them or, for Upstream staff, leaves
 * PostHog off.
 */
export function usePublicViewerAnalytics(): void {
  const isLoggedOut = useAuth().status === 'logged out';

  React.useEffect(() => {
    if (!isLoggedOut || !(isProd || CAPTURE_ME_PLEASE) || posthog.__loaded) {
      return;
    }
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      person_profiles: 'never',
      persistence: 'memory',
      ip: false,
      // PostHog's remote config can switch on session replay or surveys, which would load
      // their scripts into the embed.
      advanced_disable_decide: true,
    });
    isPublicViewer = true;
  }, [isLoggedOut]);
}

type EventProps = Record<string, string | number | boolean>;

export function recordEvent(name: Events, props?: EventProps): void {
  if (posthog.__loaded && (!isPublicViewer || PUBLIC_VIEWER_EVENTS.has(name))) {
    posthog.capture(name, props);
  }
  maybeUpdateOnboardingTasks(name);
}

function maybeUpdateOnboardingTasks(name: Events) {
  if (!analyticsAuth || !analyticsAuth.profile) {
    return;
  }
  const onboardingTasks =
    analyticsAuth.profile.getIn(['settings', 'onboardingTasks'])?.toJS() || {};

  let task: string;
  switch (name) {
    case 'Created properties from drawing':
    case 'Uploaded properties from file':
      task = 'property';
      break;
    case 'Entered compare mode':
      task = 'compare';
      break;
    case 'Ordered an image':
      task = 'order';
      break;
    case 'Created note from scratch':
    case 'Created note from Analysis':
    case 'Created note from Lookout notification':
      task = 'note';
      break;
    case 'Generated chart from Analysis':
      task = 'analysis';
      break;
    case 'Created Lookout policy':
    case 'Enabled Lookout policy':
      task = 'lookout';
      break;
    case 'Created report':
      task = 'report';
      break;
    default:
      return;
  }

  // Saving settings replaces the profile, which every logged-in view depends on,
  // so a task that's already done isn't saved again.
  if (onboardingTasks[task]) {
    return;
  }
  onboardingTasks[task] = true;
  analyticsAuth.actions.updateSettings({onboardingTasks});
}

type Events =
  // Onboarding
  | 'Completed onboarding task list'
  | 'Forced completion of onboarding task list'
  | 'Dismissed onboarding task list'

  // Agent
  | 'Updated agent settings'
  | 'Merged observations'
  | 'Kicked off agent run'

  // Lookout
  | 'Created note from Lookout notification'
  | 'Created Lookout policy'
  | 'Enabled Lookout policy'
  | 'Disabled Lookout policy'

  // Library
  | 'Layer added from layers library'
  | 'Bulk updated layers'
  | 'Followed missing layer warning'
  | 'Changed basemap'
  | 'Added dataset'
  | 'Removed dataset'
  | 'Uploaded overlay from file'
  | 'Updated overlay from file'
  | 'Added Upstream overlay'
  | 'Removed Upstream overlay'
  | 'Followed missing layer warning'
  | 'Ordered an image'
  | 'Placed a bulk order'
  | 'Downloaded GIF'
  | 'Downloaded PNG'
  | 'Downloaded GeoTIFF'
  | 'Downloaded Geospatial PDF'

  // Analysis
  | 'Opened Analysis'
  | 'Generated chart from Analysis'

  // Grid review
  | 'Opened grid review tool'
  | 'Created review grid'

  // Settings
  | 'Set notification preference'
  | 'Unset organization logo'
  | 'Set organization logo'
  | 'Set user role'
  | 'Set user name'
  | 'Deleted user'
  | 'Reset user MFA'
  | 'Set email'
  | 'Force reset credentials'

  // Notes
  | 'Created note from Analysis'
  | 'Created note from scratch'

  // Reports
  | 'Created report'
  | 'Viewed report as story'
  | 'Created story share link'
  | 'Viewed public story'
  | 'Saved report'

  // Portfolio
  | 'Archived features'
  | 'Created share link'
  | 'Set assignee'
  | 'Unset assignee'
  | 'Set report due date'
  | 'Unset report due date'
  | 'Updated properties'
  | 'Created properties from drawing'
  | 'Uploaded properties from file'
  | 'Set tag'
  | 'Unset tag'
  | 'Created multi-location property'
  | 'Split multi-location property'
  | 'Split oversized property'

  // Dashboard
  | 'Visited dashboard'
  | 'Clicked dashboard support link'

  // Controls
  | 'Entered 3D mode'
  | 'Exited 3D mode'
  | 'Opened measure distance'
  | 'Entered compare mode'
  | 'Exited compare mode'
  | 'Opened imagery timeline'
  | 'Played imagery timeline';
